/**
 * Product card badges pick their text color from the photo behind them.
 * For each [data-badge-adaptive] group, this samples the top-left corner of the
 * card's first photo (where the badges sit) from a tiny copy of the image and
 * sets data-badge-tone="dark" (white text, smoky pill) or "light" (dark text,
 * light pill). Re-checks when the photo changes, such as a swatch swap.
 */
const SAMPLE_WIDTH = 64;
const cache = new Map();

function smallUrl(src) {
  try {
    const url = new URL(src, location.href);
    url.searchParams.set('width', String(SAMPLE_WIDTH));
    url.searchParams.delete('height');
    url.searchParams.delete('crop');
    return url.toString();
  } catch {
    return null;
  }
}

function brightnessOf(src) {
  const key = smallUrl(src);
  if (!key) return Promise.resolve(null);
  if (cache.has(key)) return cache.get(key);
  const promise = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => {
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) return resolve(null);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        // Badge area: the top-left corner of the photo.
        const sw = Math.max(1, Math.round(w * 0.5));
        const sh = Math.max(1, Math.round(h * 0.22));
        const data = ctx.getImageData(0, 0, sw, sh).data;
        let total = 0;
        let count = 0;
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3] / 255;
          // Transparent pixels show the light card background.
          const r = data[i] * alpha + 245 * (1 - alpha);
          const g = data[i + 1] * alpha + 245 * (1 - alpha);
          const b = data[i + 2] * alpha + 245 * (1 - alpha);
          total += 0.2126 * r + 0.7152 * g + 0.0722 * b;
          count += 1;
        }
        resolve(count ? total / count : null);
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = key;
  });
  cache.set(key, promise);
  return promise;
}

async function update(group) {
  const card = group.parentElement;
  const photo = card?.querySelector('img');
  const src = photo?.currentSrc || photo?.src;
  if (!src) return;
  const brightness = await brightnessOf(src);
  if (brightness == null) return;
  group.dataset.badgeTone = brightness < 150 ? 'dark' : 'light';
}

const watched = new WeakSet();

function watch(group) {
  if (watched.has(group)) return;
  watched.add(group);
  update(group);
  const photo = group.parentElement?.querySelector('img');
  if (photo) {
    new MutationObserver(() => update(group)).observe(photo, { attributes: true, attributeFilter: ['src', 'srcset'] });
    photo.addEventListener('load', () => update(group));
  }
}

function scan(root = document) {
  root.querySelectorAll('[data-badge-adaptive]').forEach(watch);
}

scan();
// Cards added later (infinite scroll, filters, recommendations).
new MutationObserver((records) => {
  for (const record of records) {
    record.addedNodes.forEach((node) => {
      if (node.nodeType !== 1) return;
      if (node.matches?.('[data-badge-adaptive]')) watch(node);
      else scan(node);
    });
  }
}).observe(document.body, { childList: true, subtree: true });
